import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

const requireTransferReferenceId = (value, label) => {
  const normalized = value === null || value === undefined ? '' : String(value).trim();
  if (!normalized || normalized === 'undefined' || normalized === 'null') {
    throw new Error(`${label} reference ID is required`);
  }
};

export const getAntepartumTransferSaveErrorMessage = (error) => {
  const details = error?.status === 400 ? error?.body?.details : null;
  return Array.isArray(details) && details.length > 0
    ? details.filter(Boolean).join(' ')
    : 'Failed to save transfer';
};

const transferApi = {
  // ========================================
  // Medication Integration
  // ========================================
  

  /**
   * Search medications from master database
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of medication suggestions
   */
  searchMedications: async (query) => {
    try {
      if (query.length < 2) return [];
      
      const res = await fetch(`${config.api.url}/medications/search?query=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return [];
      }
      
      return handleResponse(res);
    } catch (error) {
      console.error('transferApi: searchMedications error:', error);
      return [];
    }
  },

  // ========================================
  // Emergency Transfer (Birth Events)
  // ========================================
  
  /**
   * Get all transfer records for a specific client
   * @param {string} clientReferenceId - Client reference ID
   * @returns {Promise<Array>} Array of transfer records
   */
  getClientTransfers: async (clientReferenceId) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/transfer`;

    try {
      const res = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        // If API is not available, return empty array for testing
        if (res.status === 404 || res.status === 500) {
          return [];
        }

        const errorData = await res.json();

        const error = new Error(errorData.message || 'Failed to fetch transfers');
        error.status = res.status;
        error.data = errorData;
        throw error;
      }

      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      // If it's a network error (API not available), return empty array
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return [];
      }

      throw error;
    }
  },

  /**
   * Initiate an emergency transfer during birth events
   * @param {string} clientReferenceId - Client reference ID
   * @param {Object} transferData - Transfer details including facility and notes
   * @returns {Promise<Object>} Transfer response
   */
  initiateEmergencyTransfer: async (clientReferenceId, transferData) => {
    const requestBody = {
      clientReferenceId: clientReferenceId,
      destinationFacilityId: transferData.destinationFacilityId,
      notes: transferData.notes || null,
      includeData: transferData.includeData || {}
    };

    const url = `${config.api.url}/clients/${clientReferenceId}/transfer`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        // If API is not available, simulate a successful transfer for testing
        if (res.status === 404 || res.status === 500) {
          return simulateTransferSuccess(clientReferenceId, transferData);
        }

        const errorData = await res.json();

        const error = new Error(errorData.message || 'Transfer failed');
        error.status = res.status;
        error.data = errorData;
        throw error;
      }

      const result = await handleResponse(res);
      return result;
    } catch (error) {
      // If it's a network error (API not available), simulate success for testing
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return simulateTransferSuccess(clientReferenceId, transferData);
      }

      throw error;
    }
  },

  // ========================================
  // Antepartum Transfers
  // ========================================
  
  /**
   * Fetch all antepartum transfers for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of antepartum transfers
   */
  fetchAntepartumTransfers: async (pregnancyId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/antepartum`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchAntepartumTransfers error:', error);
      return null;
    }
  },

  /**
   * Create a new antepartum transfer
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} transferData - Antepartum transfer data
   * @returns {Promise<Object>} Created transfer
   */
  createAntepartumTransfer: async (pregnancyId, transferData) => {
    // Transform frontend data to match backend DTO structure
    const requestBody = {
      dto: {
        careStatus: transferData.careStatus,
        careStatusDate: transferData.careStatusDate,
        dueToReason: transferData.dueToReason,
        certifiedLetterRequired: transferData.certifiedLetterRequired === 'yes',
        dateMailed: transferData.dateMailed || null,
        certifiedLetterNumber: transferData.certifiedLetterNumber || null,
        billingNotificationMethod: transferData.billingNotificationMethod || null,
        notifiedBy: transferData.notifiedBy || null,
        facilitatedTransfer: transferData.facilitatedTransfer === 'yes',
        newProviderName: transferData.newProviderName || null,
        medicalRecordReleaseSigned: transferData.medicalRecordReleaseSigned === 'yes' ? true : transferData.medicalRecordReleaseSigned === 'no' ? false : null,
        chartsSentDate: transferData.chartsSentDate || null,
        consultedWith: transferData.consultedWith || null,
        planningPostpartumReturn: transferData.planningPostpartumReturn || null,
        notes: transferData.notes || null,
        selectedTemplate: transferData.selectedTemplate || null,
        signedBy: transferData.signedBy || null,
        signatureDate: transferData.signatureDate || null,
        subsequentReturnToCare: transferData.subsequentReturnToCare || false,
        subsequentReturnDate: transferData.subsequentReturnDate || null,
        flagAsDraft: transferData.flagAsDraft || false,
        draftForProvider: transferData.draftForProvider || null
      }
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/antepartum`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing antepartum transfer
   * @param {number} transferId - Transfer ID
   * @param {Object} transferData - Updated transfer data
   * @returns {Promise<Object>} Updated transfer
   */
  updateAntepartumTransfer: async (transferId, transferData) => {
    requireTransferReferenceId(transferId, 'Antepartum transfer');

    // Transform frontend data to match backend DTO structure
    const requestBody = {
      dto: {
        careStatus: transferData.careStatus,
        careStatusDate: transferData.careStatusDate,
        dueToReason: transferData.dueToReason,
        certifiedLetterRequired: transferData.certifiedLetterRequired === 'yes',
        dateMailed: transferData.dateMailed || null,
        certifiedLetterNumber: transferData.certifiedLetterNumber || null,
        billingNotificationMethod: transferData.billingNotificationMethod || null,
        notifiedBy: transferData.notifiedBy || null,
        facilitatedTransfer: transferData.facilitatedTransfer === 'yes',
        newProviderName: transferData.newProviderName || null,
        medicalRecordReleaseSigned: transferData.medicalRecordReleaseSigned === 'yes' ? true : transferData.medicalRecordReleaseSigned === 'no' ? false : null,
        chartsSentDate: transferData.chartsSentDate || null,
        consultedWith: transferData.consultedWith || null,
        planningPostpartumReturn: transferData.planningPostpartumReturn || null,
        notes: transferData.notes || null,
        selectedTemplate: transferData.selectedTemplate || null,
        signedBy: transferData.signedBy || null,
        signatureDate: transferData.signatureDate || null,
        subsequentReturnToCare: transferData.subsequentReturnToCare || false,
        subsequentReturnDate: transferData.subsequentReturnDate || null,
        flagAsDraft: transferData.flagAsDraft || false,
        draftForProvider: transferData.draftForProvider || null
      }
    };
    
    const res = await fetch(`${config.api.url}/transfers/antepartum/${transferId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete an antepartum transfer
   * @param {number} transferId - Transfer ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteAntepartumTransfer: async (transferId) => {
    requireTransferReferenceId(transferId, 'Antepartum transfer');

    const res = await fetch(`${config.api.url}/transfers/antepartum/${transferId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Pre-Admit Referrals
  // ========================================
  
  /**
   * Fetch all pre-admit referrals for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of pre-admit referrals
   */
  fetchPreAdmitReferrals: async (pregnancyId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/pre-admit`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchPreAdmitReferrals error:', error);
      return null;
    }
  },

  /**
   * Create a new pre-admit referral
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} referralData - Pre-admit referral data
   * @returns {Promise<Object>} Created referral
   */
  createPreAdmitReferral: async (pregnancyId, referralData) => {
    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(referralData, 'pre-admit')
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/pre-admit`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing pre-admit referral
   * @param {number} referralId - Referral ID
   * @param {Object} referralData - Updated referral data
   * @returns {Promise<Object>} Updated referral
   */
  updatePreAdmitReferral: async (referralId, referralData) => {
    requireTransferReferenceId(referralId, 'Pre-admit transfer');

    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(referralData, 'pre-admit')
    };
    
    const res = await fetch(`${config.api.url}/transfers/pre-admit/${referralId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a pre-admit referral
   * @param {number} referralId - Referral ID
   * @returns {Promise<Object>} Deletion response
   */
  deletePreAdmitReferral: async (referralId) => {
    requireTransferReferenceId(referralId, 'Pre-admit transfer');

    const res = await fetch(`${config.api.url}/transfers/pre-admit/${referralId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Labor Transfers (Maternal/Newborn during birth)
  // ========================================
  
  /**
   * Fetch all labor transfers for a pregnancy
   * @param {string} pregnancyReferenceId - Pregnancy reference ID
   * @returns {Promise<Array>} Array of labor transfers
   */
  fetchLaborTransfers: async (pregnancyReferenceId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/transfers/labor-transfer`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchLaborTransfers error:', error);
      return null;
    }
  },

  /**
   * Create a new labor transfer
   * @param {string} pregnancyReferenceId - Pregnancy reference ID
   * @param {Object} transferData - Labor transfer data
   * @returns {Promise<Object>} Created transfer
   */
  createLaborTransfer: async (pregnancyReferenceId, transferData) => {
    const requestBody = {
      dto: transformLaborTransferData(transferData)
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/transfers/labor-transfer`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing labor transfer
   * @param {string} laborTransferReferenceId - Labor transfer reference ID
   * @param {Object} transferData - Updated labor transfer data
   * @returns {Promise<Object>} Updated transfer
   */
  updateLaborTransfer: async (laborTransferReferenceId, transferData) => {
    requireTransferReferenceId(laborTransferReferenceId, 'Labor transfer');

    const requestBody = {
      dto: transformLaborTransferData(transferData)
    };
    
    const res = await fetch(`${config.api.url}/transfers/labor-transfer/${laborTransferReferenceId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a labor transfer
   * @param {string} laborTransferReferenceId - Labor transfer reference ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteLaborTransfer: async (laborTransferReferenceId) => {
    requireTransferReferenceId(laborTransferReferenceId, 'Labor transfer');

    const res = await fetch(`${config.api.url}/transfers/labor-transfer/${laborTransferReferenceId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Intrapartum Transfers
  // ========================================
  
  /**
   * Fetch all intrapartum transfers for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of intrapartum transfers
   */
  fetchIntrapartumTransfers: async (pregnancyId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/intrapartum`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchIntrapartumTransfers error:', error);
      return null;
    }
  },

  /**
   * Create a new intrapartum transfer
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} transferData - Intrapartum transfer data
   * @returns {Promise<Object>} Created transfer
   */
  createIntrapartumTransfer: async (pregnancyId, transferData) => {
    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(transferData, 'intrapartum')
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/intrapartum`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing intrapartum transfer
   * @param {number} transferId - Transfer ID
   * @param {Object} transferData - Updated transfer data
   * @returns {Promise<Object>} Updated transfer
   */
  updateIntrapartumTransfer: async (transferId, transferData) => {
    requireTransferReferenceId(transferId, 'Intrapartum transfer');

    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(transferData, 'intrapartum')
    };
    
    const res = await fetch(`${config.api.url}/transfers/intrapartum/${transferId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete an intrapartum transfer
   * @param {number} transferId - Transfer ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteIntrapartumTransfer: async (transferId) => {
    requireTransferReferenceId(transferId, 'Intrapartum transfer');

    const res = await fetch(`${config.api.url}/transfers/intrapartum/${transferId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Postpartum Transfers
  // ========================================
  
  /**
   * Fetch all postpartum transfers for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of postpartum transfers
   */
  fetchPostpartumTransfers: async (pregnancyId) => {
    
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/postpartum`, {
        headers: getAuthHeaders()
      });      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchPostpartumTransfers error:', error);
      return null;
    }
  },

  /**
   * Create a new postpartum transfer
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} transferData - Postpartum transfer data
   * @returns {Promise<Object>} Created transfer
   */
  createPostpartumTransfer: async (pregnancyId, transferData) => {
    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(transferData, 'postpartum')
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/postpartum`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing postpartum transfer
   * @param {number} transferId - Transfer ID
   * @param {Object} transferData - Updated transfer data
   * @returns {Promise<Object>} Updated transfer
   */
  updatePostpartumTransfer: async (transferId, transferData) => {
    requireTransferReferenceId(transferId, 'Postpartum transfer');

    // Transform frontend data to match backend DTO structure for unified transfers
    const requestBody = {
      dto: transformUnifiedTransferData(transferData, 'postpartum')
    };
    
    const res = await fetch(`${config.api.url}/transfers/postpartum/${transferId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a postpartum transfer
   * @param {number} transferId - Transfer ID
   * @returns {Promise<Object>} Deletion response
   */
  deletePostpartumTransfer: async (transferId) => {
    requireTransferReferenceId(transferId, 'Postpartum transfer');

    const res = await fetch(`${config.api.url}/transfers/postpartum/${transferId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Neonate Transfers
  // ========================================
  
  /**
   * Fetch all neonate transfers for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of neonate transfers
   */
  fetchNeonateTransfers: async (pregnancyId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/neonate`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchNeonateTransfers error:', error);
      return null;
    }
  },

  /**
   * Create a new neonate transfer
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} transferData - Neonate transfer data
   * @returns {Promise<Object>} Created transfer
   */
  createNeonateTransfer: async (pregnancyId, transferData) => {
    // Transform frontend data to match backend DTO structure for neonate transfers
    const requestBody = {
      dto: transformNeonateTransferData(transferData)
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/neonate`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing neonate transfer
   * @param {number} transferId - Transfer ID
   * @param {Object} transferData - Updated transfer data
   * @returns {Promise<Object>} Updated transfer
   */
  updateNeonateTransfer: async (transferId, transferData) => {
    requireTransferReferenceId(transferId, 'Neonate transfer');

    // Transform frontend data to match backend DTO structure for neonate transfers
    const requestBody = {
      dto: transformNeonateTransferData(transferData)
    };
    
    const res = await fetch(`${config.api.url}/transfers/neonate/${transferId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a neonate transfer
   * @param {number} transferId - Transfer ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteNeonateTransfer: async (transferId) => {
    requireTransferReferenceId(transferId, 'Neonate transfer');

    const res = await fetch(`${config.api.url}/transfers/neonate/${transferId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Demise Documentation
  // ========================================
  
  /**
   * Fetch all demise documentations for a pregnancy
   * @param {number} pregnancyId - Pregnancy ID
   * @returns {Promise<Array>} Array of demise documentations
   */
  fetchDemiseDocumentations: async (pregnancyId) => {
    try {
      const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/demise`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        return null;
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      console.error('transferApi: fetchDemiseDocumentations error:', error);
      return null;
    }
  },

  /**
   * Create a new demise documentation
   * @param {number} pregnancyId - Pregnancy ID
   * @param {Object} demiseData - Demise documentation data
   * @returns {Promise<Object>} Created documentation
   */
  createDemiseDocumentation: async (pregnancyId, demiseData) => {
    // Transform frontend data to match backend DTO structure for demise documentation
    const requestBody = {
      dto: transformDemiseTransferData(demiseData)
    };
    
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/transfers/demise`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing demise documentation
   * @param {number} demiseId - Demise documentation ID
   * @param {Object} demiseData - Updated demise data
   * @returns {Promise<Object>} Updated documentation
   */
  updateDemiseDocumentation: async (demiseId, demiseData) => {
    requireTransferReferenceId(demiseId, 'Demise documentation');

    // Transform frontend data to match backend DTO structure for demise documentation
    const requestBody = {
      dto: transformDemiseTransferData(demiseData)
    };
    
    const res = await fetch(`${config.api.url}/transfers/demise/${demiseId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a demise documentation
   * @param {string|number} demiseId - Demise documentation reference ID (or legacy numeric ID)
   * @returns {Promise<Object>} Deletion response
   */
  deleteDemiseDocumentation: async (demiseId) => {
    requireTransferReferenceId(demiseId, 'Demise documentation');

    const res = await fetch(`${config.api.url}/transfers/demise/${demiseId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  // ========================================
  // Miscarriage Documentation
  // ========================================

  /**
   * Fetch all miscarriage documentations for a pregnancy
   * @param {string} pregnancyReferenceId - Pregnancy reference ID
   * @returns {Promise<Array>} Array of miscarriage documentations
   */
  fetchMiscarriageDocumentations: async (pregnancyReferenceId) => {
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/transfers/miscarriage`, {
      headers: getAuthHeaders()
    });
    const result = await handleResponse(res);
    return result || [];
  },

  /**
   * Create a new miscarriage documentation
   * @param {string} pregnancyReferenceId - Pregnancy reference ID
   * @param {Object} miscarriageData - Miscarriage documentation data
   * @returns {Promise<Object>} Created documentation
   */
  createMiscarriageDocumentation: async (pregnancyReferenceId, miscarriageData) => {
    const requestBody = {
      dto: transformMiscarriageTransferData(miscarriageData)
    };

    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/transfers/miscarriage`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    return handleResponse(res);
  },

  /**
   * Update an existing miscarriage documentation
   * @param {string} miscarriageReferenceId - Miscarriage documentation reference ID
   * @param {Object} miscarriageData - Updated miscarriage data
   * @returns {Promise<Object>} Updated documentation
   */
  updateMiscarriageDocumentation: async (miscarriageReferenceId, miscarriageData) => {
    requireTransferReferenceId(miscarriageReferenceId, 'Miscarriage documentation');

    const requestBody = {
      dto: transformMiscarriageTransferData(miscarriageData)
    };

    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageReferenceId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    return handleResponse(res);
  },

  finalizeMiscarriageDocumentation: async (miscarriageReferenceId, miscarriageData, clientStatus) => {
    requireTransferReferenceId(miscarriageReferenceId, 'Miscarriage documentation');

    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageReferenceId}/finalize`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        dto: transformMiscarriageTransferData(miscarriageData),
        clientStatus
      })
    });

    return handleResponse(res);
  },

  /**
   * Delete a miscarriage documentation
   * @param {string} miscarriageReferenceId - Miscarriage documentation reference ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteMiscarriageDocumentation: async (miscarriageReferenceId) => {
    requireTransferReferenceId(miscarriageReferenceId, 'Miscarriage documentation');

    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageReferenceId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });

    return handleResponse(res);
  },

  // ========================================
  // Facility Management
  // ========================================
  
  /**
   * Fetch all available facilities for transfers (scoped to organization when provided)
   * @param {number|string} [organizationId] - Organization/practice ID; when set, GET includes ?organizationId=
   * @returns {Promise<Array>} Array of facilities
   */
  fetchFacilities: async (organizationId) => {
    try {
      const base = `${config.api.url}/facilities`;
      const url =
        organizationId != null && organizationId !== ''
          ? `${base}?organizationId=${encodeURIComponent(organizationId)}`
          : base;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        // Return test facility data when API is not available (only when unscoped for local dev fallback)
        if (organizationId == null || organizationId === '') {
          return getTestFacilities();
        }
        return [];
      }
      
      const result = await handleResponse(res);
      return result || [];
    } catch (error) {
      if (organizationId == null || organizationId === '') {
        return getTestFacilities();
      }
      return [];
    }
  },

  /**
   * Fetch a specific facility by ID
   * @param {number} facilityId - Facility ID
   * @returns {Promise<Object>} Facility data
   */
  fetchFacility: async (facilityId) => {
    const res = await fetch(`${config.api.url}/facilities/${facilityId}`, {
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  },

  /**
   * Create a new facility
   * @param {Object} facilityData - Facility fields; include `organizationId` for practice-scoped facilities
   * @returns {Promise<Object>} Created facility
   */
  createFacility: async (facilityData) => {
    const res = await fetch(`${config.api.url}/facilities`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(facilityData)
    });
    
    return handleResponse(res);
  },

  /**
   * Update an existing facility
   * @param {number} facilityId - Facility ID
   * @param {Object} facilityData - Updated facility data
   * @returns {Promise<Object>} Updated facility
   */
  updateFacility: async (facilityId, facilityData) => {
    // Ensure the facility data includes the ID for backend validation
    const facilityWithId = {
      ...facilityData,
      id: facilityId
    };
    
    const res = await fetch(`${config.api.url}/facilities/${facilityId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(facilityWithId)
    });
    
    return handleResponse(res);
  },

  /**
   * Delete a facility
   * @param {number} facilityId - Facility ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteFacility: async (facilityId) => {
    const res = await fetch(`${config.api.url}/facilities/${facilityId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    return handleResponse(res);
  }
};

// ========================================
// Data Transformation Functions
// ========================================

/**
 * Transform UnifiedTransferForm data to backend DTO structure
 * Used for Pre-Admit, Intrapartum, and Postpartum transfers
 */
const transformUnifiedTransferData = (transferData, transferType = null) => {
  return {
    // Transfer Type (for backend routing/validation)
    transferType: transferType || transferData.transferType || null,
    
    // Vitals & Encounter Info
    encounterDate: transferData.encounterDate || null,
    encounterTime: transferData.encounterTime || null,
    emergency: transferData.emergency === true || transferData.emergency === 'true',
    bloodPressure: transferData.bloodPressure || null,
    pulse: transferData.pulse ? parseInt(transferData.pulse) : null,
    temperature: transferData.temperature ? parseFloat(transferData.temperature) : null,
    
    // Pregnancy History (GTPAL)
    gravida: transferData.gravida ? parseInt(transferData.gravida) : null,
    term: transferData.term ? parseInt(transferData.term) : null,
    preterm: transferData.preterm ? parseInt(transferData.preterm) : null,
    abortions: transferData.abortions ? parseInt(transferData.abortions) : null,
    living: transferData.living ? parseInt(transferData.living) : null,
    
    // Lab Data
    hctHgbData: transferData.hctHgbData || [],
    rhFactor: transferData.rhFactor || null,
    bloodType: transferData.bloodType || null,
    groupBStrep: transferData.groupBStrep || null,
    
    // Allergies
    drugAllergies: transferData.drugAllergies || null,
    adhesiveAllergies: transferData.adhesiveAllergies || null,
    latexAllergies: transferData.latexAllergies || null,
    otherAllergies: transferData.otherAllergies || null,
    
    // Medications (now handled via snapshots - this field will be populated by backend)
    medicationSnapshots: transferData.medicationSnapshots || [],
    
    // Transfer Summaries
    summaryBeforeTransfer: transferData.summaryBeforeTransfer || null,
    includeBeforeInPrintout: transferData.includeBeforeInPrintout === true || transferData.includeBeforeInPrintout === 'true',
    beforeTemplate: transferData.beforeTemplate || null,
    
    summaryAfterTransfer: transferData.summaryAfterTransfer || null,
    includeAfterInPrintout: transferData.includeAfterInPrintout === true || transferData.includeAfterInPrintout === 'true',
    afterTemplate: transferData.afterTemplate || null,
    
    // Additional Fields
    chartReviewInstructions: transferData.chartReviewInstructions || null,
    subsequentReturnToCare: transferData.subsequentReturnToCare === true || transferData.subsequentReturnToCare === 'true',
    returnToCareDate: transferData.returnToCareDate || null,
    
    // Form Actions
    flagAsDraft: transferData.flagAsDraft === true || transferData.flagAsDraft === 'true',
    draftForProvider: transferData.draftForProvider || null,
    providerRole: transferData.providerRole || 'rendering'
  };
};

/**
 * Transform LaborTransferForm data to backend DTO structure
 */
// Coerce a value to a plain YYYY-MM-DD string for System.DateOnly, or null.
// Handles empty strings, full ISO datetime strings ("2025-03-14T00:00:00"), and nulls.
const toDateOnly = (value) => {
  if (!value) return null;
  const str = String(value).split('T')[0];
  return str || null;
};

// Coerce a value to an HH:mm:ss time string for System.TimeOnly, or null.
// HTML time inputs produce "HH:mm"; TimeOnly's JSON deserializer requires "HH:mm:ss".
const toTimeOnly = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  // "HH:mm" → "HH:mm:ss", already "HH:mm:ss" stays as-is
  const parts = str.split(':');
  if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
  if (parts.length >= 3) return `${parts[0]}:${parts[1]}:${parts[2]}`;
  return null;
};

const transformLaborTransferData = (transferData) => {
  return {
    maternalTransfer: transferData.maternalTransfer === true || transferData.maternalTransfer === 'true',
    newbornTransfer: transferData.newbornTransfer === true || transferData.newbornTransfer === 'true',
    selectedBabies: Array.isArray(transferData.selectedBabies) ? transferData.selectedBabies : [],
    hospitalId: transferData.hospitalId ? parseInt(transferData.hospitalId) : null,
    hospitalName: transferData.hospitalName || null,
    providerType: transferData.providerType || null,
    providerName: transferData.providerName || null,
    transferDate: toDateOnly(transferData.transferDate),
    transferTime: toTimeOnly(transferData.transferTime),
    transportMethod: transferData.transportMethod || null,
    transportMethodOther: transferData.transportMethodOther || null,
    birthOccurred: transferData.birthOccurred || null,
    birthType: transferData.birthType || null,
    babySex: transferData.babySex || null,
    babyDOB: toDateOnly(transferData.babyDOB),
    babyTimeOfBirth: toTimeOnly(transferData.babyTimeOfBirth),
    birthEventReferenceId: transferData.birthEventReferenceId || transferData.BirthEventReferenceId || null,
    babyReferenceId: transferData.babyReferenceId || transferData.BabyReferenceId || transferData.createdBabyReferenceId || null,
    nicuAdmission: transferData.nicuAdmission || null,
    nicuName: transferData.nicuName || null,
    nicuLocation: transferData.nicuLocation || null,
    accompanyingProviders: Array.isArray(transferData.accompanyingProviders) ? transferData.accompanyingProviders : [],
    noProviderAccompanied: transferData.noProviderAccompanied === true || transferData.noProviderAccompanied === 'true',
    transferReason: transferData.transferReason || null,
    transferReasonOther: transferData.transferReasonOther || null,
    transferSummary: transferData.transferSummary || null
  };
};

/**
 * Transform NeonateTransferForm data to backend DTO structure
 */
const transformNeonateTransferData = (transferData) => {
  return {
    // Encounter Info
    encounterDate: transferData.encounterDate || null,
    encounterTime: transferData.encounterTime || null,
    emergency: transferData.emergency === true || transferData.emergency === 'true',
    respiration: transferData.respiration ? parseInt(transferData.respiration) : null,
    pulse: transferData.pulse ? parseInt(transferData.pulse) : null,
    temperature: transferData.temperature ? parseFloat(transferData.temperature) : null,
    oxygenSaturation: transferData.oxygenSaturation ? parseInt(transferData.oxygenSaturation) : null,
    
    // Birth Information
    presentation: transferData.presentation || null,
    oxygen: transferData.oxygen === true || transferData.oxygen === 'true',
    firstBreathTime: transferData.firstBreathTime || null,
    chestCompressions: transferData.chestCompressions === true || transferData.chestCompressions === 'true',
    suction: transferData.suction || null,
    placeOfBirth: transferData.placeOfBirth || null,
    ppv: transferData.ppv === true || transferData.ppv === 'true',
    motherGBS: transferData.motherGBS || null,
    
    // Baby Information
    babyDOB: transferData.babyDOB || null,
    babyDOBTime: transferData.babyDOBTime || null,
    babySex: transferData.babySex || null,
    weightGrams: transferData.weightGrams ? parseInt(transferData.weightGrams) : null,
    lengthInches: transferData.lengthInches ? parseFloat(transferData.lengthInches) : null,
    headCircumference: transferData.headCircumference ? parseFloat(transferData.headCircumference) : null,
    chestCircumference: transferData.chestCircumference ? parseFloat(transferData.chestCircumference) : null,
    apgar1Min: transferData.apgar1Min ? parseInt(transferData.apgar1Min) : null,
    apgar5Min: transferData.apgar5Min ? parseInt(transferData.apgar5Min) : null,
    apgar10Min: transferData.apgar10Min ? parseInt(transferData.apgar10Min) : null,
    
    // Transfer Summaries
    summaryBeforeTransfer: transferData.summaryBeforeTransfer || null,
    includeBeforeInPrintout: transferData.includeBeforeInPrintout === true || transferData.includeBeforeInPrintout === 'true',
    beforeTemplate: transferData.beforeTemplate || null,
    
    summaryAfterTransfer: transferData.summaryAfterTransfer || null,
    includeAfterInPrintout: transferData.includeAfterInPrintout === true || transferData.includeAfterInPrintout === 'true',
    afterTemplate: transferData.afterTemplate || null,
    
    // Follow-Up
    subsequentReturnToCare: transferData.subsequentReturnToCare === true || transferData.subsequentReturnToCare === 'true',
    returnToCareDate: transferData.returnToCareDate || null,
    
    // Form Actions
    flagAsDraft: transferData.flagAsDraft === true || transferData.flagAsDraft === 'true',
    draftForProvider: transferData.draftForProvider || null,
    providerRole: transferData.providerRole || 'rendering'
  };
};

/**
 * Transform DemiseTransferForm data to backend DTO structure
 */
const transformDemiseTransferData = (transferData) => {
  return {
    // Demise Type
    demiseType: transferData.demiseType || 'fetal',
    
    // Fetal Demise Section
    fetalDemiseDate: transferData.fetalDemiseDate || null,
    diagnosedByUltrasound: transferData.diagnosedByUltrasound || null,
    fhtPresentAtLabor: transferData.fhtPresentAtLabor || null,
    fetalDemiseWeeks: transferData.fetalDemiseWeeks ? parseInt(transferData.fetalDemiseWeeks) : null,
    fetalDemiseDays: transferData.fetalDemiseDays ? parseInt(transferData.fetalDemiseDays) : null,
    fetalCauseKnown: transferData.fetalCauseKnown || null,
    fetalCauseSuspected: transferData.fetalCauseSuspected || null,
    fetalCauseUnknown: transferData.fetalCauseUnknown === true || transferData.fetalCauseUnknown === 'true',
    
    // Neonatal Demise Section
    neonatalDemiseDate: transferData.neonatalDemiseDate || null,
    demisePredicted: transferData.demisePredicted || null,
    neonatalDemiseWeeks: transferData.neonatalDemiseWeeks ? parseInt(transferData.neonatalDemiseWeeks) : null,
    neonatalDemiseDays: transferData.neonatalDemiseDays ? parseInt(transferData.neonatalDemiseDays) : null,
    neonatalCauseKnown: transferData.neonatalCauseKnown || null,
    neonatalCauseSuspected: transferData.neonatalCauseSuspected || null,
    neonatalCauseUnknown: transferData.neonatalCauseUnknown === true || transferData.neonatalCauseUnknown === 'true',
    
    // Transport & Location Details
    transportOccurred: transferData.transportOccurred || null,
    deathLocation: transferData.deathLocation || null,
    deathTiming: transferData.deathTiming || null,
    
    // Notes
    notes: transferData.notes || null,
    selectedTemplate: transferData.selectedTemplate || null,
    
    // Form Actions
    flagAsDraft: transferData.flagAsDraft === true || transferData.flagAsDraft === 'true',
    draftForProvider: transferData.draftForProvider || null,
    providerRole: transferData.providerRole || 'rendering'
  };
};

/**
 * Transform MiscarriageDocumentationForm data to backend DTO structure
 */
const transformMiscarriageTransferData = (transferData) => {
  return {
    expectedRevision: transferData.revision ?? transferData.expectedRevision ?? null,
    // Section 1 — Pregnancy Status
    pregnancyStatus: transferData.pregnancyStatus || null,

    // Section 2 — Gestational Age at Loss
    gestationalAgeWeeks: transferData.gestationalAgeWeeks ? parseInt(transferData.gestationalAgeWeeks) : null,
    gestationalAgeDays: transferData.gestationalAgeDays ? parseInt(transferData.gestationalAgeDays) : null,

    // Section 3 — Key Dates
    dateMiscarriageSuspected: transferData.dateMiscarriageSuspected || null,
    dateMiscarriageConfirmed: transferData.dateMiscarriageConfirmed || null,
    dateTissuePassed: transferData.dateTissuePassed || null,

    // Section 4 — Location of Loss
    locationOfLoss: transferData.locationOfLoss || null,
    locationOfLossOther: transferData.locationOfLoss === 'other' ? (transferData.locationOfLossOther || null) : null,

    // Section 5 — Management Type
    managementType: transferData.managementType || null,
    medications: Array.isArray(transferData.medications) ? transferData.medications : [],
    medicationOther: transferData.medicationOther || null,

    // Section 6 — Symptoms at Presentation
    symptoms: Array.isArray(transferData.symptoms) ? transferData.symptoms : [],
    symptomNarrative: transferData.symptomNarrative || null,

    // Section 7 — Bleeding Assessment
    bleedingAmount: transferData.bleedingAmount || null,
    padsSoakedPerHour: transferData.padsSoakedPerHour || null,
    clotsPresent: transferData.clotsPresent || null,
    clotSize: transferData.clotsPresent === 'yes' ? (transferData.clotSize || null) : null,
    bleedingNotes: transferData.bleedingNotes || null,

    // Section 8 — Tissue Passage
    tissuePassage: transferData.tissuePassage || null,
    tissueDescription: transferData.tissueDescription || null,

    // Section 9 — Ultrasound Findings
    ultrasoundFindings: transferData.ultrasoundFindings || null,
    ultrasoundSummary: transferData.ultrasoundSummary || null,

    // Section 10 — Lab Evaluation
    hcgTrend: transferData.hcgTrend || null,
    hcgValuesAndDates: transferData.hcgValuesAndDates || null,
    bloodType: transferData.bloodType || null,
    rhProphylaxis: transferData.bloodType === 'rh_negative' ? (transferData.rhProphylaxis || null) : null,

    // Section 11 — Complications
    complications: Array.isArray(transferData.complications) ? transferData.complications : [],
    complicationNotes: transferData.complicationNotes || null,

    // Section 12 — Transfer of Care
    transferOfCare: transferData.transferOfCare || null,
    receivingFacilityProvider: transferData.receivingFacilityProvider || null,

    // Section 13 — Emotional Support
    emotionalSupport: Array.isArray(transferData.emotionalSupport) ? transferData.emotionalSupport : [],
    supportNotes: transferData.supportNotes || null,

    // Section 14 — Provider Narrative
    providerNarrative: transferData.providerNarrative || null,

    // Section 15 — Client Status Update
    clientStatusUpdate: transferData.clientStatusUpdate || null,

    // Form Actions
    flagAsDraft: transferData.flagAsDraft === true || transferData.flagAsDraft === 'true',
    draftForProvider: transferData.draftForProvider || null,
    providerRole: transferData.providerRole || 'rendering',
    clinicalCourseEntries: Array.isArray(transferData.clinicalCourseEntries)
      ? transferData.clinicalCourseEntries.map(entry => ({
          referenceId: entry.referenceId || null,
          occurredAtUtc: entry.occurredAtUtc || new Date().toISOString(),
          milestone: entry.milestone || null,
          symptoms: Array.isArray(entry.symptoms) ? entry.symptoms : [],
          bleedingAmount: entry.bleedingAmount || null,
          tissuePassage: entry.tissuePassage || null,
          narrative: entry.narrative || ''
        }))
      : []
  };
};

// ========================================
// Helper Functions
// ========================================

// Test facility data for development/testing
const getTestFacilities = () => {
  return [
    {
      id: 1,
      name: "Denver General Hospital",
      faxNumber: "+13035551234",
      address: "1635 Aurora Court",
      city: "Aurora", 
      state: "CO",
      zipCode: "80045",
      phone: "+13035551200",
      email: "transfers@denvergeneral.org",
      isActive: true,
      facilityType: "Level III Trauma Center",
      transferNotes: "Emergency transfers accepted 24/7. Direct line to L&D: ext 4567. Ask for Dr. Martinez for high-risk transfers.",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-15T10:30:00Z"
    },
    {
      id: 2,
      name: "Presbyterian/St. Joseph Hospital",
      faxNumber: "+13035552345",
      address: "1719 E 19th Ave",
      city: "Denver",
      state: "CO", 
      zipCode: "80218",
      phone: "+13035552300",
      email: "maternity@presbystjoseph.org",
      isActive: true,
      facilityType: "Level II Birthing Center",
      transferNotes: "Specializes in maternal-fetal medicine. 24/7 NICU available. Contact Dr. Sarah Chen for complex cases.",
      createdAt: "2024-01-02T00:00:00Z",
      updatedAt: "2024-01-20T14:15:00Z"
    },
    {
      id: 3,
      name: "National Jewish Health",
      faxNumber: "+13035553456",
      address: "1400 Jackson St",
      city: "Denver",
      state: "CO",
      zipCode: "80206", 
      phone: "+13035553400",
      email: "emergency@njhealth.org",
      isActive: true,
      facilityType: "Specialty Hospital",
      transferNotes: "Respiratory and pulmonary specialists. Limited obstetric services - use for respiratory complications only.",
      createdAt: "2024-01-03T00:00:00Z",
      updatedAt: "2024-01-25T09:45:00Z"
    },
    {
      id: 4,
      name: "Rose Medical Center",
      faxNumber: "+13035554567",
      address: "9191 Grant St",
      city: "Thornton",
      state: "CO",
      zipCode: "80229",
      phone: "+13035554500",
      email: "labor-delivery@rosemedical.com",
      isActive: true,
      facilityType: "Full Service Hospital",
      transferNotes: "Full labor & delivery unit with Level II NICU. Excellent for routine and moderate-risk transfers. Dr. Kim leads maternity services.",
      createdAt: "2024-01-04T00:00:00Z",
      updatedAt: "2024-02-01T16:20:00Z"
    },
    {
      id: 5,
      name: "Porter Adventist Hospital",
      faxNumber: "+13035555678", 
      address: "2525 S Downing St",
      city: "Denver",
      state: "CO",
      zipCode: "80210",
      phone: "+13035555600",
      email: "obstetrics@porteradventist.org",
      isActive: true,
      facilityType: "Community Hospital",
      transferNotes: "Community-focused care with experienced midwifery-friendly staff. Good for low-intervention transfers. Contact Linda Thompson, CNM.",
      createdAt: "2024-01-05T00:00:00Z",
      updatedAt: "2024-02-05T11:30:00Z"
    },
    {
      id: 6,
      name: "Boulder Community Health",
      faxNumber: "+13035556789",
      address: "1100 Balsam Ave", 
      city: "Boulder",
      state: "CO",
      zipCode: "80304",
      phone: "+13035556700",
      email: "transfers@bch.org", 
      isActive: false,
      facilityType: "Regional Medical Center",
      transferNotes: "Currently undergoing renovation - not accepting transfers until March 2024.",
      createdAt: "2024-01-06T00:00:00Z",
      updatedAt: "2024-02-10T08:15:00Z"
    }
  ];
};

// Simulate transfer success for testing
const simulateTransferSuccess = (clientReferenceId, transferData) => {
  
  // Simulate a realistic delay
  return new Promise((resolve) => {
    setTimeout(() => {
      const testFacilities = getTestFacilities();
      const selectedFacility = testFacilities.find(f => f.id === transferData.destinationFacilityId);
      
      resolve({
        message: "Transfer initiated successfully",
        transferId: `TEST_${Date.now()}`,
        facility: selectedFacility?.name,
        faxNumber: selectedFacility?.faxNumber,
        timestamp: new Date().toISOString()
      });
    }, 1500); // 1.5 second delay to simulate processing
  });
};

export default transferApi;
